import { formatInTimezone } from '../timezone/utils';
export const CREDIT_PAGE_SIZE_OPTIONS = [20, 50, 100];
export const defaultCreditLedgerFilters = () => ({
    period: 'today',
    dateFrom: '',
    dateTo: '',
    typeFilter: 'all',
    search: '',
    sort: 'created',
    sortDir: 'desc',
});
export function creditLedgerCreatedAt(row, timezone = Intl.DateTimeFormat().resolvedOptions().timeZone) {
    var _a;
    const v = (_a = row.createdAt) !== null && _a !== void 0 ? _a : row.created_at;
    return formatInTimezone(v, timezone);
}
/** Compact created display: date line + 24h time line (e.g. May 22, 2026 / 12:03). */
export function creditLedgerCreatedAtCompact(row, timezone = Intl.DateTimeFormat().resolvedOptions().timeZone) {
    var _a;
    const v = (_a = row.createdAt) !== null && _a !== void 0 ? _a : row.created_at;
    if (!v)
        return null;
    const d = new Date(v);
    if (Number.isNaN(d.getTime()))
        return null;
    const dateLine = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        month: 'short',
        day: '2-digit',
        year: 'numeric',
    }).format(d);
    const timeLine = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
    }).format(d);
    return { dateLine, timeLine };
}
/** Optional reference/source metadata when distinct from payment method. */
export function creditLedgerSourceLabel(row) {
    var _a, _b, _c, _d;
    const refType = String((_b = (_a = row.referenceType) !== null && _a !== void 0 ? _a : row.reference_type) !== null && _b !== void 0 ? _b : '').trim();
    const refId = String((_d = (_c = row.referenceId) !== null && _c !== void 0 ? _c : row.reference_id) !== null && _d !== void 0 ? _d : '').trim();
    if (!refType && !refId)
        return null;
    const typeLabel = refType ? refType.replace(/_/g, ' ') : '';
    const candidate = refId
        ? typeLabel
            ? `${typeLabel}: ${refId}`
            : refId
        : typeLabel;
    if (!candidate)
        return null;
    const pm = creditLedgerPaymentMethodLabel(row);
    if (pm !== '—' && candidate.toLowerCase() === pm.toLowerCase())
        return null;
    return candidate;
}
export function creditLedgerTypeLabel(row) {
    var _a, _b;
    const lt = String((_a = row.ledgerType) !== null && _a !== void 0 ? _a : '').toLowerCase();
    if (lt === 'deposit')
        return 'Deposit';
    if (lt === 'withdraw')
        return 'Withdraw';
    const t = String((_b = row.type) !== null && _b !== void 0 ? _b : '').toLowerCase();
    if (t === 'credit')
        return 'Deposit';
    if (t === 'debit')
        return 'Withdraw';
    return '—';
}
export function creditLedgerSignedAmount(row) {
    var _a, _b;
    if (row.signedAmount != null && Number.isFinite(Number(row.signedAmount))) {
        return Number(row.signedAmount);
    }
    const lt = String((_b = (_a = row.ledgerType) !== null && _a !== void 0 ? _a : row.type) !== null && _b !== void 0 ? _b : '').toLowerCase();
    const amt = Math.abs(Number(row.amount) || 0);
    if (lt === 'deposit' || lt === 'credit')
        return amt;
    if (lt === 'withdraw' || lt === 'debit')
        return -amt;
    return Number(row.amount) || 0;
}
export function formatCreditLedgerAmount(row) {
    const signed = creditLedgerSignedAmount(row);
    const abs = Math.abs(signed);
    const formatted = abs.toLocaleString(undefined, { maximumFractionDigits: 8 });
    if (signed > 1e-9)
        return `+${formatted}`;
    if (signed < -1e-9)
        return `-${formatted}`;
    return formatted;
}
export function formatCreditBalance(v) {
    if (v === undefined || v === null || v === '')
        return '—';
    const n = Number(v);
    if (!Number.isFinite(n))
        return '—';
    return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 8 });
}
export function creditLedgerDisplayStatus(row) {
    return String(row.status || 'completed').toLowerCase();
}
export function creditLedgerStatusLabel(status) {
    const s = String(status || 'completed').toLowerCase();
    if (s === 'completed')
        return 'Completed';
    if (s === 'pending')
        return 'Pending';
    if (s === 'failed')
        return 'Failed';
    if (s === 'cancelled')
        return 'Cancelled';
    return status ? String(status) : '—';
}
export function creditLedgerStatusBadgeClass(status) {
    const s = String(status || 'completed').toLowerCase();
    if (s === 'completed')
        return 'bg-emerald-100 text-emerald-800';
    if (s === 'pending')
        return 'bg-amber-100 text-amber-800';
    if (s === 'failed' || s === 'cancelled')
        return 'bg-red-100 text-red-800';
    return 'bg-gray-100 text-gray-700';
}
export function creditLedgerPaymentMethodLabel(row) {
    const pm = row.paymentMethod;
    if (pm == null || String(pm).trim() === '')
        return '—';
    return String(pm).trim();
}
export function partyLabel(party) {
    var _a, _b, _c;
    if (!party)
        return '—';
    if (party.label)
        return party.label;
    const role = (_a = party.roleLabel) !== null && _a !== void 0 ? _a : 'Account';
    const name = (_c = (_b = party.displayName) !== null && _b !== void 0 ? _b : party.ownerId) !== null && _c !== void 0 ? _c : 'Unknown';
    return `${role}: ${name}`;
}
export { buildCompactPageRange } from '../pagination/pageRange';
export function validateCustomPeriod(dateFrom, dateTo) {
    if (!dateFrom.trim())
        return 'From date is required.';
    if (!dateTo.trim())
        return 'To date is required.';
    if (dateFrom > dateTo)
        return 'From must be on or before To.';
    return null;
}
function formatIsoDateLocal(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}
/** Resolve calendar date range from period preset or custom inputs. */
export function resolveCreditPeriodDateRange(period, dateFrom, dateTo) {
    const now = new Date();
    if (period === 'today') {
        const t = formatIsoDateLocal(now);
        return { dateFrom: t, dateTo: t };
    }
    if (period === '7d') {
        const from = new Date(now);
        from.setDate(from.getDate() - 6);
        return { dateFrom: formatIsoDateLocal(from), dateTo: formatIsoDateLocal(now) };
    }
    if (period === '30d') {
        const from = new Date(now);
        from.setDate(from.getDate() - 29);
        return { dateFrom: formatIsoDateLocal(from), dateTo: formatIsoDateLocal(now) };
    }
    return { dateFrom: dateFrom.trim(), dateTo: dateTo.trim() };
}
export const CREDIT_LEDGER_MAX_EXPORT_DAYS = 35;
/** Export date range: custom period validation + max inclusive calendar days. */
export function validateCreditExportPeriod(dateFrom, dateTo) {
    const base = validateCustomPeriod(dateFrom, dateTo);
    if (base)
        return base;
    const from = new Date(`${dateFrom.trim()}T00:00:00`);
    const to = new Date(`${dateTo.trim()}T00:00:00`);
    if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime()))
        return 'Invalid date.';
    const inclusiveDays = Math.floor((to.getTime() - from.getTime()) / (24 * 60 * 60 * 1000)) + 1;
    if (inclusiveDays > CREDIT_LEDGER_MAX_EXPORT_DAYS) {
        return `Date range cannot exceed ${CREDIT_LEDGER_MAX_EXPORT_DAYS} days.`;
    }
    return null;
}
